// The small signs that say the console is working (4 Nov 2026):
// - TopProgress: the thin blue line at the top of the window while a read
//   someone asked for is running. It waits 180 ms before showing, so a fast
//   read never flashes it, creeps towards the end while it waits, then runs to
//   the end and fades.
// - Fresh: « mis à jour 02:41 » with a small ring while a refresh runs (shown
//   only past 400 ms, so the 30 s refresh does not blink) and « pas actualisé »
//   when one failed.
// - NetBanner: no connection, said once at the bottom, and « de retour » when
//   it comes back.
// - ErrorState: a read that failed, in words, with « Réessayer » busy while it
//   tries again.
import { useEffect, useRef, useState } from 'react';
import { S } from '../lib/strings';
import { hhmm } from '../lib/format';
import { useBusy } from '../lib/progress';
import { useOnline } from '../lib/net';
import { Ic } from './Icon';

export function TopProgress() {
  const busy = useBusy();
  const [phase, setPhase] = useState<'idle' | 'run' | 'done'>('idle');
  const shown = useRef(false);
  useEffect(() => {
    if (busy) {
      const t = setTimeout(() => { shown.current = true; setPhase('run'); }, 180);
      return () => clearTimeout(t);
    }
    if (!shown.current) return;
    shown.current = false;
    setPhase('done');
    const t = setTimeout(() => setPhase('idle'), 420);
    return () => clearTimeout(t);
  }, [busy]);
  return (
    <div className={'topbar ' + phase} role="progressbar" aria-label={S.loading} aria-hidden={phase === 'idle'} aria-busy={phase === 'run'}>
      <i />
    </div>
  );
}

export const Spinner = ({ s = 14 }: { s?: number }) => <span className="ring" style={{ width: s, height: s }} aria-hidden="true" />;

export function Fresh({ at, refreshing, failed, onRetry }: { at: Date | null; refreshing?: boolean; failed?: boolean; onRetry?(): void }) {
  if (failed) {
    return (
      <span className="fresh bad" role="status">
        <i className="fr-dot" />{at ? S.ld.notSince(hhmm(at)) : S.ld.notRefreshed}
        {onRetry && <button className="link" onClick={onRetry}>{S.errors.retry}</button>}
      </span>
    );
  }
  return <span className={'fresh' + (refreshing ? ' busy' : '')}>{at ? S.updatedAt(hhmm(at)) : S.upToDate}<Spinner s={11} /></span>;
}

export function NetBanner({ at }: { at: Date | null }) {
  const online = useOnline();
  const [back, setBack] = useState(false);
  const was = useRef(online);
  useEffect(() => {
    if (online && !was.current) { setBack(true); const t = setTimeout(() => setBack(false), 2600); was.current = online; return () => clearTimeout(t); }
    was.current = online;
  }, [online]);
  if (online && !back) return null;
  return (
    <div className={'netbar' + (online ? ' ok' : '')} role="status" aria-live="polite">
      <Ic n={online ? 'check' : 'wifi'} s="xs" />
      {online ? <b>{S.ld.back}</b> : <><b>{S.ld.offline}</b><span>{S.ld.offlineSub(at ? hhmm(at) : null)}</span></>}
    </div>
  );
}

export function ErrorState({ error, onRetry, retrying, inline }: { error: string; onRetry?(): void; retrying?: boolean; inline?: boolean }) {
  const online = useOnline();
  const card = (
    <div className="card errst" role="alert">
      <span className="errst-ic"><Ic n={online ? 'alert' : 'wifi'} s="s" /></span>
      <b>{online ? S.errors.load : S.ld.noNet}</b>
      <span className="muted">{S.ld.checkNet}</span>
      <code className="mono">{error}</code>
      {onRetry && <button className="btn sm" onClick={onRetry} aria-busy={retrying} disabled={retrying}><Ic n="refresh" s="xs" />{S.errors.retry}</button>}
    </div>
  );
  return inline ? card : <section className="view on">{card}</section>;
}
